/**
 * Composable untuk Settings CRUD admin (paginated list dengan search).
 *
 * Berbeda dengan useMaster.js yang fokus dropdown (pagination=false, 1 jam),
 * file ini paginated dengan search dan stale 30 detik.
 *
 * Pemakaian:
 *   const params = reactive({ page: 1, limit: 10, search: "" });
 *   const { data, isLoading } = useBranchAdminList(computed(() => params));
 *
 * Auto-refetch saat page/limit/search berubah (TanStack tracks via queryKey).
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchBranchAdminList,
  fetchBankAdminList,
  fetchBankCourierAdminList,
  fetchCourierAdminList,
  fetchServiceAdminList,
  fetchPickupAdminList,
  fetchJobAdminList,
  fetchCostCategoryAdminList,
  fetchRoleAdminList,
  fetchKaryawanAdminList,
  fetchUserAdminList,
  fetchAgenAdminList,
  fetchMemberAdminList,
  fetchDestinationAdminList,
} from "@/queries/api/masterAdmin";

const LIST_STALE = 30 * 1000; // 30 detik

function p(v) {
  return unref(v);
}

function adminListQuery(subkey, fn, params) {
  return useQuery({
    queryKey: computed(() => [...keys.master.all, "admin", subkey, p(params)]),
    queryFn: () => fn(p(params)),
    staleTime: LIST_STALE,
  });
}

export function useBranchAdminList(params = {}) {
  return adminListQuery("branch-list", fetchBranchAdminList, params);
}
export function useBankAdminList(params = {}) {
  return adminListQuery("bank-list", fetchBankAdminList, params);
}
export function useBankCourierAdminList(params = {}) {
  return adminListQuery("bank-courier-list", fetchBankCourierAdminList, params);
}
export function useCourierAdminList(params = {}) {
  return adminListQuery("courier-list", fetchCourierAdminList, params);
}
export function useServiceAdminList(params = {}) {
  return adminListQuery("service-list", fetchServiceAdminList, params);
}
export function usePickupAdminList(params = {}) {
  return adminListQuery("pickup-list", fetchPickupAdminList, params);
}
export function useJobAdminList(params = {}) {
  return adminListQuery("job-list", fetchJobAdminList, params);
}
export function useCostCategoryAdminList(params = {}) {
  return adminListQuery(
    "cost-category-list",
    fetchCostCategoryAdminList,
    params
  );
}
export function useRoleAdminList(params = {}) {
  return adminListQuery("role-list", fetchRoleAdminList, params);
}
export function useKaryawanAdminList(params = {}) {
  return adminListQuery("karyawan-list", fetchKaryawanAdminList, params);
}
export function useUserAdminList(params = {}) {
  return adminListQuery("user-list", fetchUserAdminList, params);
}
export function useAgenAdminList(params = {}) {
  return adminListQuery("agen-list", fetchAgenAdminList, params);
}
export function useMemberAdminList(params = {}) {
  return adminListQuery("member-list", fetchMemberAdminList, params);
}
export function useDestinationAdminList(params = {}) {
  return adminListQuery("destination-list", fetchDestinationAdminList, params);
}
